import { gql, QueryOptions } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import client from 'api/ApolloClient';
import { MessageMedium } from 'modules/graphql/types';
import { addNegativeToast } from 'modules/toastContainer';

import { SavedRow } from '../types';
import { useTrackDragAndDropEditorRUMError } from './useTrackDragAndDropEditorRUMError';

const QUERY_SAVED_ROWS_ERROR_MESSAGE = 'Unable to fetch saved rows';

export type QuerySavedRowsVariables = {
  filterInfo?: {
    categoryId?: number;
    createdByUserId?: string;
    updatedByUserId?: string;
    messageMedium?: MessageMedium;
  };
};

export type QuerySavedRowsResponse = {
  savedRows: SavedRow[];
};

export const querySavedRows = gql`
  query getSavedRows(
    $filterInfo: SavedRowFilterInfoInput
    $pagination: Pagination
    $sort: Sort
    $search: String
  ) {
    savedRows(filterInfo: $filterInfo, pagination: $pagination, sort: $sort, search: $search) {
      id
      name
      messageMedium
      category {
        id
        name
        projectId
        createdAt
        updatedAt
        createdBy
        updatedBy
      }
      creatorUser {
        id
        fullName
        avatarUrl
      }
      updatedByUser {
        id
        fullName
        avatarUrl
      }
      createdAt
      updatedAt
      primaryContent {
        id
        html
        beeBuilderMetadata
        creatorUser {
          id
          fullName
          avatarUrl
        }
        createdAt
      }
    }
  }
`;

export const useQuerySavedRows = (
  options?: Partial<QueryOptions<QuerySavedRowsVariables, QuerySavedRowsResponse>>,
) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [savedRows, setSavedRows] = useState<SavedRow[]>([]);

  const dispatch = useDispatch();
  const trackError = useTrackDragAndDropEditorRUMError();

  const getSavedRows = useCallback(async (variables: QuerySavedRowsVariables) => {
    try {
      setIsLoading(true);
      const response = await client.query<QuerySavedRowsResponse, QuerySavedRowsVariables>({
        query: querySavedRows,
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        variables,
        ...options,
      });

      if ((response.data as any)?.errors) {
        throw new Error(JSON.stringify((response.data as any)?.errors)); // this is handled by the catch block
      } else {
        setSavedRows(response.data?.savedRows ?? []);
        setError(null);
      }

      return response;
    } catch (err: any) {
      setError(err);
      dispatch(addNegativeToast(QUERY_SAVED_ROWS_ERROR_MESSAGE));
      trackError({ error: err, errorMessage: QUERY_SAVED_ROWS_ERROR_MESSAGE });
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    error,
    isLoading,
    savedRows,
    getSavedRows,
  };
};
